/* global DEBUG */
import { FADE_DURATION } from "./consts";

/**
 * In some browsers, play returns a promise.
 * We want to catch any errors and hide them.
 *
 * @param    {Event} e error object from promise.
 */
function catchPlaybackError(e) {
  if (DEBUG) {
    //eslint-disable-next-line no-console
    console.warn(e);
  }
}

/**
 * Play the video, capturing any errors from the promise in
 * browsers which support it.
 *
 * @param    {HTMLElement} video HTML5 video element
 * @returns  {Function}    function to pause playback
 */
export function playVideo(video) {
  if (video.paused) {
    try {
      video.volume = 1;

      const pr = video.play();
      if (pr.catch) {
        pr.catch(catchPlaybackError);
      }
    } catch (ex) {
      if (DEBUG) {
        //eslint-disable-next-line no-console
        console.warn("error", ex);
      }
    }
  }

  return {
    thenPause() {
      try {
        video.pause();
        video.currentTime = 0; // Safari desktop won't pause without this ?!?!
      } catch (ex) {
        //
      }
    }
  };
}

/**
 * When the player is about to end playback, trigger the callback.
 *
 * @param      {HTMLElement} video HTML video element.
 * @returns    {Function}    function that is run when the video is about
 *                           to end. Pass in a callback.
 */
function atEnd(video) {
  return function videoEnding(callback) {
    video.ontimeupdate = () => {
      if (video.currentTime > video.duration - FADE_DURATION / 1000) {
        this.ontimeupdate = null;
        callback();
      }
    };
  };
}

/**
 * Pause a video in a try catch and log any error.
 *
 * @param    {HTMLElement} video Video element
 */
export function pauseVideo(video) {
  try {
    video.pause();
  } catch (ex) {
    if (DEBUG) {
      //eslint-disable-next-line no-console
      console.warn(ex);
    }
  }
}

/**
 * Do something whilst playback continues.
 *
 * @param    {HTMLElement} video HTML video element.
 * @returns  {object}                  function that is run when the video
 *                                       can play. Pass in a callback.
 */
function whilstPlaying(video) {
  return function whilstVideoPlays(callback) {
    const now = new Date().getTime();

    if (video.readyState > 2) {
      setTimeout(callback, FADE_DURATION);
      return chainedPlaybackFunction(video);
    }
    video.oncanplay = () => {
      video.oncanplay = null;
      const delay = FADE_DURATION - new Date().getTime() - now;
      setTimeout(callback, delay);
    };

    return chainedPlaybackFunction(video);
  };
}

/**
 * Chained function to play the video element.
 *
 * @param    {HTMLElement} video HTML video element
 * @returns  {Function}                  Plays the video when ready.
 */
function andPlay(video) {
  return function playTheVideo() {
    playVideo(video);

    return chainedPlaybackFunction(video);
  };
}

/**
 * Returns an object of functions bound to passed in player
 *
 * @param    {HTMLElement} video Video element
 * @returns  {object}                    Playback functions
 */
export function chainedPlaybackFunction(video) {
  return {
    andPlay: andPlay(video),
    atEnd: atEnd(video),
    whilstPlaying: whilstPlaying(video)
  };
}
